/** CRREM Technical Blueprint v1.1. Pure annual calculations; no WEii options or UI rounding. */
export type CrremMetric = 'energy' | 'carbon';
export interface CrremPathway { name: string; energy: number[]; carbon: number[]; }
export interface CrremRegion { country: string; grid: number[]; types: Record<string, CrremPathway>; }
export interface CrremReference {
    version: string; firstYear: number; lastYear: number;
    regions: Record<string, CrremRegion>; gasFactor: number; uk2020GridFactor: number;
}
export interface CrremEnergy {
    grid: number; gas: number; heat: number; cold: number; selfConsumed: number; exported: number;
}
export interface CrremFactors { grid: number; gas: number; heat: number; cold: number; }
export interface CrremYear {
    year: number; energy: CrremEnergy; eui: number; carbon: number | null;
    warnings: string[]; coverage: Record<string, number>; estimated: boolean;
}
export interface CrremAssessment {
    area: number; region: CrremRegion | null; pathway: CrremPathway | null;
    years: CrremYear[]; warnings: string[]; buildingName: string; pathwayApproximate: boolean;
}

export function energyIntensity(e: CrremEnergy, area: number): number {
    return (e.grid + e.gas + e.heat + e.cold + e.selfConsumed) / area;
}
export function carbonIntensity(e: CrremEnergy, area: number, ef: CrremFactors): number {
    // Solar export can offset only grid electricity; never combustion or district energy.
    return (Math.max(0, e.grid - e.exported) * ef.grid + e.gas * ef.gas
        + e.heat * ef.heat + e.cold * ef.cold) / area;
}
export function emissionFactors(ref: CrremReference, region: CrremRegion, year: number, baseYear: number): CrremFactors {
    const grid = region.grid[year - ref.firstYear];
    const heat = 0.20431 * grid / ref.uk2020GridFactor;
    // Blueprint 1.1: cooling fallback stays at reporting-year level, heating tracks the grid.
    const cold = 0.20431 * region.grid[baseYear - ref.firstYear] / ref.uk2020GridFactor;
    return {grid, gas: ref.gasFactor, heat, cold};
}
export function firstExceedance(points: {year: number; value: number; target: number}[]): number | null {
    return points.find(p => p.value > p.target)?.year ?? null;
}
const normalise = (s: string) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim();
export interface CrremBuildingTypeMapping { code: string; approximate: boolean; }
/** Reviewed catalog aliases; approximate references are disclosed separately in the UI. */
const directBuildingTypes: Record<string, string[]> = {
    OFF: ['office', 'kantoor'],
    HOT: ['hotel'],
    HEC: ['healthcare', 'hospital', 'ziekenhuis', 'care home with overnight stay', 'tehuis met overnachting',
        'verpleeghuis', 'verzorgingshuis', 'medical group practice', 'medical practice',
        'medische groepspraktijk', 'medische groeps praktijk', 'daycare without overnight stay', 'opvang zonder overnachting'],
    DWC: ['cold store', 'koel vrieshuis', 'koelhuis', 'distribution warehouse cold', 'distribution warehouse cooled'],
    DWW: ['distribution warehouse warm', 'verwarmd distributiecentrum'],
    RHS: ['retail high street'],
    RSM: ['shopping center', 'shopping centre', 'retail shopping center', 'retail shopping centre'],
    RWB: ['retail warehouse'],
    RMF: ['residential multi family', 'apartment building', 'appartementengebouw', 'single house apartment',
        'enkele woning appartement', 'woongebouw'],
    RSF: ['residential single family', 'single house ground level', 'enkele woning grondgebonden',
        'grondgebonden woning', 'eengezinswoning'],
    LEI: ['leisure lodging', 'lodging leisure recreation', 'indoor sports accommodation',
        'binnensportaccommodatie', 'sportaccommodatie binnen', 'sporthal', 'outdoor sports facilities',
        'sportaccommodatie buiten', 'swimming pool', 'zwembad', 'sauna', 'holiday park', 'vakantiepark']
};
const approximateBuildingTypes: Record<string, string[]> = {
    LEI: ['restaurant', 'café', 'other', 'overig'],
    OFF: ['childcare', 'kinderopvang', 'primary secondary education', 'basis voorgezet onderwijs',
        'basis voortgezet onderwijs', 'university higher vocational training intermediate vocational training',
        'universiteit hbo mbo'],
    HEC: ['cell building', 'cellengebouw'],
    DWW: ['production hall', 'bedrijfshal', 'productiehal'],
    RWB: ['car garage showroom', 'garage showroom'],
    RHS: ['shop with goods refrigeration', 'winkel met warenkoeling',
        'shop without goods refrigeration', 'winkel zonder warenkoeling']
};
const buildingTypeMappings = new Map<string, CrremBuildingTypeMapping>();
for (const [approximate, groups] of [[false, directBuildingTypes], [true, approximateBuildingTypes]] as const) {
    for (const [code, aliases] of Object.entries(groups)) {
        for (const alias of approximate ? aliases : [code, ...aliases]) {
            buildingTypeMappings.set(normalise(alias), {code, approximate});
        }
    }
}
export function resolveBuildingTypeMapping(name: string, code = ''): CrremBuildingTypeMapping | null {
    // Prefer the stable catalog code when display names have been translated or customized.
    return buildingTypeMappings.get(normalise(code)) || buildingTypeMappings.get(normalise(name)) || null;
}
export function mapBuildingType(name: string, code = ''): string | null {
    return resolveBuildingTypeMapping(name, code)?.code || null;
}
export function resolveRegion(ref: CrremReference, country: string): CrremRegion | null {
    const aliases: Record<string,string> = {nederland:'NL',netherlands:'NL',belgie:'BE',belgium:'BE',
        duitsland:'DE',germany:'DE',frankrijk:'FR',france:'FR', 'united kingdom':'UK', gb:'UK'};
    const key = aliases[normalise(country)] || country?.toUpperCase();
    return ref.regions[key] || Object.values(ref.regions).find(r => normalise(r.country) === normalise(country)) || null;
}
const types = {
    grid: ['electricityConsumption','electricityConsumptionOffPeak'], exported: ['electricityFeedIn','electricityFeedInOffPeak'],
    production: ['electricityGrossProduction'], gas: ['gasConsumption'], heat: ['heatConsumption'], cold: ['coolingConsumption']
};
export function assessCrrem(ref: CrremReference, input: {
    area: number; country: string; buildingName: string; buildingCode?: string; allElectric: boolean;
    productionExpected: boolean; measurements: any; currentYear: number;
}): CrremAssessment {
    const region = resolveRegion(ref, input.country);
    const mapping = resolveBuildingTypeMapping(input.buildingName, input.buildingCode);
    const mapped = mapping?.code;
    // Canonical CRREM names are accepted only when actually present for the selected country.
    const pathway = (mapped && region?.types[mapped]) || Object.values(region?.types || {})
        .find(p => normalise(p.name) === normalise(input.buildingName)) || null;
    const warnings: string[] = [];
    if (!(input.area > 0)) warnings.push('crrem_missing_area');
    if (!region) warnings.push('crrem_missing_region');
    if (!pathway) warnings.push('crrem_missing_type');
    const byYear = new Map<number, {totals: Record<string,number>; months: Record<string,Set<number>>; estimated: boolean; invalid: boolean}>();
    for (const [estimated, values] of [[false,input.measurements?.measurements], [true,input.measurements?.estimatedMeasurements]] as const) {
        for (const [type, points] of Object.entries(values || {})) {
            if (!Object.values(types).some(list => list.includes(type))) continue;
            for (const p of points as any[]) {
                const year = Number(p.timeRange?.start?.slice(0,4));
                if (year < ref.firstYear || year >= input.currentYear || year > ref.lastYear || !Number.isInteger(year)) continue;
                const row = byYear.get(year) || {totals:{},months:{},estimated:false,invalid:false};
                const value = Number(p.value);
                if (!Number.isFinite(value) || value < 0) row.invalid = true;
                else {
                    row.totals[type] = (row.totals[type] || 0) + value;
                    (row.months[type] ||= new Set()).add(Number(p.timeRange.start.slice(5,7)));
                }
                row.estimated ||= estimated;
                byYear.set(year,row);
            }
        }
    }
    const years: CrremYear[] = [];
    if (input.area > 0) for (const [year,row] of [...byYear].sort((a,b) => a[0]-b[0])) {
        const sum = (key: keyof typeof types) => types[key].reduce((s,t) => s+(row.totals[t] || 0),0);
        const coverage: Record<string,number> = {};
        for (const [key,list] of Object.entries(types)) coverage[key] = new Set(list.flatMap(t => [...(row.months[t] || [])])).size;
        const issues: string[] = [];
        if (row.invalid) issues.push('crrem_invalid_values');
        if (coverage.grid < 12) issues.push('crrem_incomplete_electricity');
        if (!input.allElectric && coverage.gas === 0 && coverage.heat === 0) issues.push('crrem_missing_heat');
        if (!input.allElectric && ((coverage.gas > 0 && coverage.gas < 12) || (coverage.heat > 0 && coverage.heat < 12)
            || (coverage.cold > 0 && coverage.cold < 12))) issues.push('crrem_incomplete_thermal');
        if (input.allElectric && sum('gas') > 0) issues.push('crrem_electric_gas_conflict');
        const hasProduction = input.productionExpected || sum('production')>0 || sum('exported')>0;
        if (hasProduction && coverage.production < 12) issues.push('crrem_missing_production');
        if (sum('production') > 0 && sum('exported') > sum('production')) issues.push('crrem_export_exceeds_production');
        if (row.estimated) issues.push('crrem_estimated');
        if (!input.allElectric && sum('gas')>0) issues.push('crrem_gas_conversion');
        if (!input.allElectric && (sum('heat')>0 || sum('cold')>0)) issues.push('crrem_thermal_factor_estimated');
        const energy: CrremEnergy = {grid:sum('grid'), exported:sum('exported'),
            selfConsumed:Math.max(0,sum('production')-sum('exported')),
            gas:input.allElectric?0:sum('gas')*9.78,
            heat:input.allElectric?0:sum('heat')*1000/3.6,
            cold:input.allElectric?0:sum('cold')*1000/3.6};
        years.push({year,energy,coverage,warnings:issues,estimated:row.estimated,eui:energyIntensity(energy,input.area),
            carbon:region?.grid.length===31?carbonIntensity(energy,input.area,emissionFactors(ref,region,year,year)):null});
    }
    return {area:input.area,region,pathway,years,warnings,buildingName:input.buildingName || input.buildingCode || '',
        pathwayApproximate:!!pathway && !!mapping?.approximate};
}
export function projectCrrem(ref: CrremReference, assessment: CrremAssessment, base: CrremYear, metric: CrremMetric) {
    if (!assessment.pathway || (metric==='carbon' && !assessment.region?.grid.length)) return [];
    return Array.from({length:ref.lastYear-base.year+1},(_,i) => {
        const year=base.year+i;
        return {year,target:assessment.pathway[metric][year-ref.firstYear],value:metric==='energy'?base.eui:
            carbonIntensity(base.energy,assessment.area,emissionFactors(ref,assessment.region,year,base.year))};
    });
}
